import React, { useEffect, useMemo, useRef, useState } from 'react';
import { HiChevronDown } from 'react-icons/hi';
import { FiAlertTriangle } from 'react-icons/fi';
import { ENTITY, setProjectPeriod, setProjectPeriodAuto, setRoadmapPeriod, setRoadmapPeriodAuto, setViewType } from "../../redux/slices/timeline";
import { useDispatch, useSelector } from "react-redux";
import { DashboardStatus, getDashboardStatusColor } from "../../constants/integrationTypes";
import { ViewType } from "../Timeline/types";
import moment from 'moment';
import { Tooltip } from "flowbite-react";
import { AnimatedNumber as AnimatedNumberShared, AnimatedWidthSlot } from "../../components/common/Animated";
import BestFitPeriodSelector from './BestFitPeriodSelector';

type TimelineNavBarProps = {
  stats: any;
  date?: string;
  period: ViewType;
  setPeriod: (period: ViewType) => void;
  isLoading?: boolean;
  simFlag?: boolean;
  status?: string;
  sim_data?: {
    stats?: any;
    status?: string;
    date?: string;
    start_date?: string;
    end_date?: string;
  };
};

// Skeleton component with global colors
const Skeleton = ({ width, height = "h-4", className = "" }: { width: string; height?: string; className?: string }) => (
  <div className={`${height} ${width} bg-light-surfaceSecondary dark:bg-gray-700 rounded animate-pulse ${className}`}></div>
);

const ICON_SLOT_PX = 18;

const TimelineNavBar: React.FC<TimelineNavBarProps> = ({
  stats = {},
  date = "-",
  period = 'monthly',
  setPeriod = () => { },
  isLoading = false,
  simFlag = false,
  status,
  sim_data
}) => {
  const dispatch = useDispatch();
  const [indicatorStyle, setIndicatorStyle] = useState({ width: 0, left: 0 });
  const [showAllStatuses, setShowAllStatuses] = useState(false);
  const timelineButtonRef = useRef<HTMLButtonElement>(null);
  const listButtonRef = useRef<HTMLButtonElement>(null);

  const {
    singleRoadmapRes,
    integration_data: dataApiIntegrationData,
  } = useSelector((state: any) => state.roadmap);

  const {
    viewType,
    currentEntity
  } = useSelector((state: any) => state.timeline);
  const { template_id } = useSelector((state: any) => state.templates);
  const isTemplate = !!template_id;

  const roadmapState = useSelector((state: any) => state.roadmap);
  
  const {
      roadmapPeriod,
      projectPeriod,
      roadmapPeriodAuto,
      projectPeriodAuto,
  } = useSelector((state: any) => state.timeline);

  useEffect(() => {
    const saved_period = localStorage.getItem("timeline_state");
    const saved_auto_storage = localStorage.getItem("timeline_auto_state") ?? "true";
    const saved_auto = saved_auto_storage==="true" ? true : false;

    // Only set if we have saved values, otherwise let Redux defaults or other logic take over
    if (saved_period || saved_auto_storage) {
        if(currentEntity === ENTITY.ROADMAP){
          if(saved_period) dispatch(setRoadmapPeriod(saved_period));
          // Only dispatch if explicitly set in storage
          if(saved_auto_storage !== null) dispatch(setRoadmapPeriodAuto(saved_auto));
        }else{
          if(saved_period) dispatch(setProjectPeriod(saved_period));
          if(saved_auto_storage !== null) dispatch(setProjectPeriodAuto(saved_auto));
        }
    }
  }, [currentEntity]);

  const autoMode = currentEntity === ENTITY.ROADMAP ? roadmapPeriodAuto : projectPeriodAuto;
  
  const handleAutoModeChange = (val: boolean) => {
    // Update local storage
    localStorage.setItem("timeline_auto_state", val ? "true" : "false");

    if (currentEntity === ENTITY.ROADMAP) {
      dispatch(setRoadmapPeriodAuto(val));
    } else {
      dispatch(setProjectPeriodAuto(val));
    }
  };

  const handlePeriodChange = (period: ViewType) => {
     // Update local storage
     localStorage.setItem("timeline_state", period);

     if (currentEntity === ENTITY.ROADMAP) {
         dispatch(setRoadmapPeriod(period));
     } else {
         dispatch(setProjectPeriod(period));
     }
     
     // Call original setPeriod if needed, though mostly used for local overrides in parent
     setPeriod(period);
  };

  const integrationType = useMemo(() => {
    if (currentEntity === ENTITY.ROADMAP) {
      return singleRoadmapRes?.integration_data?.integration_type;
    } else if (currentEntity === ENTITY.PROJECT) {
      return dataApiIntegrationData?.integration_type;
    }

    return null
  }, [currentEntity, singleRoadmapRes, dataApiIntegrationData])

  const isJiraRoadmap = integrationType === "jira"

  const displayStatsFromProps = simFlag && sim_data?.stats ? sim_data.stats : stats;
  const statusFromProps = simFlag && sim_data?.status ? sim_data.status : status;
  const effectiveStats = (displayStatsFromProps && Object.keys(displayStatsFromProps).length > 0)
    ? displayStatsFromProps
    : (roadmapState?.stats || {});

  const isAtRisk = ((statusFromProps || '') as string).toLowerCase() === DashboardStatus.AT_RISK;
  const showCueDelayed = (currentEntity === ENTITY.PROJECT)
    ? Number(effectiveStats?.delayed) > 0
    : (isAtRisk && Number(effectiveStats?.delayed) > 0);
  const showCueUnallocated = (currentEntity === ENTITY.PROJECT)
    ? Number(effectiveStats?.unallocated) > 0
    : (isAtRisk && Number(effectiveStats?.unallocated) > 0);
  const showCueBlocked = (currentEntity === ENTITY.PROJECT)
    ? Number(effectiveStats?.blocked) > 0
    : (isAtRisk && Number(effectiveStats?.blocked) > 0);

  const simDateRange = (() => {
    if (!simFlag) return null;
    if (sim_data?.date) return sim_data.date;
    if (sim_data?.start_date && sim_data?.end_date) return `${sim_data.start_date} - ${sim_data.end_date}`;
    return null;
  })();

  const displayDate = (() => {
    if (simDateRange) return simDateRange;
    if (date && date !== "-") return date;
    if (roadmapState?.start_date && roadmapState?.end_date) {
      return `${roadmapState.start_date} - ${roadmapState.end_date}`;
    }
    return date;
  })();

  const handleViewChange = (view: "timeline" | "list") => {
    dispatch(setViewType(view))
  };

  const updateIndicator = () => {
    const activeButton = viewType === 'timeline' ? timelineButtonRef.current : listButtonRef.current;
    if (activeButton) {
      setIndicatorStyle({
        width: activeButton.offsetWidth,
        left: activeButton.offsetLeft
      });
    }
  };

  const formatDateRange = (dateString: string) => {
    if (!dateString || dateString === "-") return dateString;

    const formatSingleDate = (dateStr: string) => {
      // Try parsing with specific formats in order of preference
      const formats = [
        'YYYY-MM-DDTHH:mm:ss',
        'YYYY-MM-DD',
        'DD/MM/YYYY',
        'MM/DD/YYYY'
      ];

      for (const format of formats) {
        const parsed = moment(dateStr.trim(), format, true); // strict parsing
        if (parsed.isValid()) {
          return parsed.format('MM/DD/YYYY');
        }
      }

      // Fallback to default parsing if none of the specific formats work
      const fallback = moment(dateStr.trim());
      return fallback.isValid() ? fallback.format('MM/DD/YYYY') : dateStr;
    };

    // Check if it's a date range (contains " - ")
    if (dateString.includes(" - ")) {
      const [startDate, endDate] = dateString.split(" - ");
      const formattedStart = formatSingleDate(startDate);
      const formattedEnd = formatSingleDate(endDate);
      return `${formattedStart} - ${formattedEnd}`;
    } else {
      // Single date
      return formatSingleDate(dateString);
    }
  };

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      updateIndicator();
    });

    return () => cancelAnimationFrame(frame);
  }, [viewType]);

  useEffect(() => {
    const handleResize = () => {
      updateIndicator();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [viewType]);

  // Render skeleton statistics for different entity types
  const renderSkeletonStats = () => {
    if (isTemplate) {
      return null;
    }
    if (currentEntity === ENTITY.PROJECT) {
      return (
        <>
          <div className="flex flex-col items-start gap-1">
            <span className="text-light-text-secondary dark:text-gray-300">Delayed</span>
            <div className="flex items-center">
              <div className={`w-3 h-3 rounded-full ${getDashboardStatusColor(DashboardStatus.DELAYED)}`}></div>
              <Skeleton width="w-6" height="h-4" className="ml-1" />
            </div>
          </div>

          <div className="flex flex-col items-start gap-1">
            <span className="text-light-text-secondary dark:text-gray-300">Unallocated</span>
            <div className="flex items-center">
              <div className={`w-3 h-3 rounded-full ${getDashboardStatusColor(DashboardStatus.UNALLOCATED)}`}></div>
              <Skeleton width="w-6" height="h-4" className="ml-1" />
            </div>
          </div>

          <div className="flex flex-col items-start gap-1">
            <span className="text-light-text-secondary dark:text-gray-300">Blocked</span>
            <div className="flex items-center">
              <div className={`w-3 h-3 rounded-full ${getDashboardStatusColor(DashboardStatus.BLOCKED)}`}></div>
              <Skeleton width="w-6" height="h-4" className="ml-1" />
            </div>
          </div>

          <div className="flex flex-col items-start gap-1">
            <span className="text-light-text-secondary dark:text-gray-300 inline-flex items-center gap-1">
              Others
              <HiChevronDown className="w-3 h-3 -rotate-90" />
            </span>
            <div className="flex items-center">
              <div
                className="w-3 h-3 rounded-full"
                style={{
                  background: 'conic-gradient(#3b82f6 0% 25%, #8b5cf6 25% 50%, #22c55e 50% 75%, #6b7280 75% 100%)'
                }}
              ></div>
              <Skeleton width="w-8" height="h-4" className="ml-1" />
            </div>
          </div>
          {/* <div className="flex flex-col items-start gap-1">
            <span className="text-light-text-secondary dark:text-gray-300">Running</span>
            <div className="flex items-center">
              <div className={`w-3 h-3 rounded-full ${getDashboardStatusColor(DashboardStatus.RUNNING)}`}></div>
              <Skeleton width="w-6" height="h-4" className="ml-1" />
            </div>
          </div>
          <div className="flex flex-col items-start gap-1">
            <span className="text-light-text-secondary dark:text-gray-300">Waiting</span>
            <div className="flex items-center">
              <div className={`w-3 h-3 rounded-full ${getDashboardStatusColor(DashboardStatus.WAITING)}`}></div>
              <Skeleton width="w-6" height="h-4" className="ml-1" />
            </div>
          </div> */}
        </>
      );
    } else if (currentEntity === ENTITY.ROADMAP) {
      return (
      <>
            <div className="flex flex-col items-start gap-1">
              <span className="text-light-text-secondary dark:text-gray-300">At Risk</span>
              <div className="flex items-center">
                <div className={`w-3 h-3 rounded-full ${getDashboardStatusColor(DashboardStatus.AT_RISK)}`}></div>
                <Skeleton width="w-6" height="h-4" className="ml-1" />
              </div>
            </div>
            <div className="flex flex-col items-start gap-1">
              <span className="text-light-text-secondary dark:text-gray-300">On Track</span>
              <div className="flex items-center">
                <div className={`w-3 h-3 rounded-full ${getDashboardStatusColor(DashboardStatus.ON_TRACK)}`}></div>
                <Skeleton width="w-6" height="h-4" className="ml-1" />
              </div>
            </div>
            <div className="flex flex-col items-start gap-1">
              <span className="text-light-text-secondary dark:text-gray-300">Completed</span>
              <div className="flex items-center">
                <div className={`w-3 h-3 rounded-full ${getDashboardStatusColor(DashboardStatus.COMPLETED)}`}></div>
                <Skeleton width="w-6" height="h-4" className="ml-1" />
              </div>
            </div>
            <div className="flex flex-col items-start gap-1">
              <span className="text-light-text-secondary dark:text-gray-300 inline-flex items-center gap-1">
                Others
                <HiChevronDown className="w-3 h-3 -rotate-90" />
              </span>
              <div className="flex items-center">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{
                    background: 'conic-gradient(#3b82f6 0% 25%, #8b5cf6 25% 50%, #22c55e 50% 75%, #6b7280 75% 100%)'
                  }}
                ></div>
                <Skeleton width="w-8" height="h-4" className="ml-1" />
              </div>
            </div>
        </>
      );
    }
    return null;
  };

  return (
    <div
      className={`relative w-full flex justify-between items-center px-4 py-2 h-[75px] dark:border-none border ${
        simFlag
          ? "bg-light-headerSimulation border-gray-300 dark:border-gray-700 dark:bg-[#00a86b1c]"
          : "bg-light-header border-gray-300 dark:border-gray-700 dark:bg-secondary-dark"
      } transition-colors duration-200`}
    >
      {/* Left side - View switcher */}
      <div className="flex items-center">
        <div className=" shadow-inner relative inline-flex bg-light-surfacePrimary  dark:bg-slate-700 rounded-lg border border-light-border-default dark:border-gray-600">
          {/* Animated indicator */}
          <div
            className="absolute h-full bg-primary-light dark:bg-primary-dark rounded-lg transition-all duration-300 ease-in-out shadow-lg"
            style={{
              width: indicatorStyle.width,
              left: indicatorStyle.left,
              zIndex: 1,
            }}
          />

          <button
            ref={timelineButtonRef}
            onClick={() => handleViewChange("timeline")}
            className={`relative px-3.5 py-1.5 text-sm font-medium transition-colors duration-300 rounded-lg ${
              viewType === "timeline"
                ? "text-light-text-inverse"
                : "text-light-text-primary  dark:text-gray-300 hover:text-light-text-secondary"
            }`}
            style={{ zIndex: 2 }}
          >
            Timeline view
          </button>

          <button
            ref={listButtonRef}
            onClick={() => handleViewChange("list")}
            className={`relative px-3.5 py-1.5 text-sm font-medium transition-colors duration-300 rounded-lg ${
              viewType === "list"
                ? "text-light-text-inverse"
                : "text-light-text-primary dark:text-gray-300 hover:text-light-text-secondary"
            }`}
            style={{ zIndex: 2 }}
          >
            List view
          </button>
        </div>

        {viewType === "timeline" && (
          <BestFitPeriodSelector
            period={period}
            setPeriod={handlePeriodChange}
            simFlag={simFlag}
            autoMode={autoMode}
            onAutoModeChange={handleAutoModeChange}
          />
        )}
      </div>

      <div className="flex items-start space-x-6 text-sm whitespace-nowrap">
        {isLoading ? (
          // Skeleton state
          <>
            {renderSkeletonStats()}
            <div className="flex flex-col items-start ml-3 gap-1.5 min-w-[185px]">
              <span className="text-light-text-secondary dark:text-gray-300 text-xs">
                Start and end date
              </span>
              <Skeleton width="w-44" height="h-5" />
            </div>
          </>
        ) : (
          // Real data state
          <>
            {!isTemplate &&
              currentEntity === ENTITY.PROJECT &&
              Object.keys(effectiveStats).length > 0 && (
                <>
                  <div
                    className={`flex items-start gap-4 transition-all duration-300 ease-out `}
                  >
                    <div className="flex flex-col items-start gap-1">
                      <span className="text-light-text-secondary dark:text-gray-300 inline-flex items-center gap-1">
                        <AnimatedWidthSlot
                          show={showCueDelayed}
                          width={ICON_SLOT_PX}
                        >
                          <Tooltip
                            content="Contributes to Risk"
                            placement="top"
                          >
                            <FiAlertTriangle className="text-red-500 transition-all duration-300 opacity-100 scale-100" />
                          </Tooltip>
                        </AnimatedWidthSlot>
                        Delayed
                      </span>
                      <div
                        className={`flex items-center ${!showCueDelayed ? "ml-1" : ""}`}
                      >
                        <div
                          className={`w-3 h-3 rounded-full ${getDashboardStatusColor(DashboardStatus.DELAYED)}`}
                        ></div>
                        <AnimatedNumberShared
                          className="ml-2 text-light-text-primary dark:text-gray-300 font-medium"
                          value={Number(effectiveStats.delayed) || 0}
                        />
                      </div>
                    </div>

                    <div className="flex flex-col items-start gap-1">
                      <span className="text-light-text-secondary dark:text-gray-300 inline-flex items-center gap-1">
                        <AnimatedWidthSlot
                          show={showCueUnallocated}
                          width={ICON_SLOT_PX}
                        >
                          <Tooltip
                            content="Contributes to Risk"
                            placement="top"
                          >
                            <FiAlertTriangle className="text-red-500 transition-all duration-300 opacity-100 scale-100" />
                          </Tooltip>
                        </AnimatedWidthSlot>
                        Unallocated
                      </span>
                      <div
                        className={`flex items-center ${!showCueUnallocated ? "ml-1" : ""}`}
                      >
                        <div
                          className={`w-3 h-3 rounded-full ${getDashboardStatusColor(DashboardStatus.UNALLOCATED)}`}
                        ></div>
                        <AnimatedNumberShared
                          className="ml-2 text-light-text-primary dark:text-gray-300 font-medium"
                          value={Number(effectiveStats.unallocated) || 0}
                        />
                      </div>
                    </div>

                    <div className="flex flex-col items-start gap-1">
                      <span className="text-light-text-secondary dark:text-gray-300 inline-flex items-center gap-1">
                        <AnimatedWidthSlot
                          show={showCueBlocked}
                          width={ICON_SLOT_PX}
                        >
                          <Tooltip
                            content="Contributes to Risk"
                            placement="top"
                          >
                            <FiAlertTriangle className="text-red-500 transition-all duration-300 opacity-100 scale-100" />
                          </Tooltip>
                        </AnimatedWidthSlot>
                        Blocked
                      </span>
                      <div
                        className={`flex items-center ${!showCueBlocked ? "ml-1" : ""}`}
                      >
                        <div
                          className={`w-3 h-3 rounded-full ${getDashboardStatusColor(DashboardStatus.BLOCKED)}`}
                        ></div>
                        <AnimatedNumberShared
                          className="ml-2 text-light-text-primary dark:text-gray-300 font-medium"
                          value={Number(effectiveStats.blocked) || 0}
                        />
                      </div>
                    </div>

                    {/* Others expandable section */}
                    {!showAllStatuses && (
                      <div className="relative">
                        <button
                          onClick={() => setShowAllStatuses(!showAllStatuses)}
                          className="flex flex-col items-start gap-1 hover:opacity-80 transition-opacity duration-200"
                        >
                          <span className="text-light-text-secondary dark:text-gray-300 inline-flex items-center gap-1">
                            Others
                            <HiChevronDown className="w-3 h-3 -rotate-90" />
                          </span>
                          <div className="flex items-center">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{
                                background:
                                  "conic-gradient(#3b82f6 0% 25%, #8b5cf6 25% 50%, #22c55e 50% 75%, #6b7280 75% 100%)",
                              }}
                            ></div>
                            <AnimatedNumberShared
                              className="ml-2 text-light-text-primary dark:text-gray-300 font-medium"
                              value={
                                (Number(effectiveStats.completed) || 0) +
                                (Number(effectiveStats.not_started) || 0) +
                                (Number(effectiveStats.on_track) || 0) +
                                (Number(effectiveStats.closed) || 0)
                              }
                            />
                          </div>
                        </button>
                      </div>
                    )}

                    {/* Expanded statuses - slide in from right */}
                    {showAllStatuses && (
                      <div className="flex items-start gap-4 transition-all duration-300 ease-out">
                        <div className="flex flex-col items-start gap-1">
                          <span className="text-light-text-secondary dark:text-gray-300">
                            Completed
                          </span>
                          <div className="flex items-center">
                            <div
                              className={`w-3 h-3 rounded-full ${getDashboardStatusColor(DashboardStatus.COMPLETED)}`}
                            ></div>
                            <AnimatedNumberShared
                              className="ml-2 text-light-text-primary dark:text-gray-300 font-medium"
                              value={Number(effectiveStats.completed) || 0}
                            />
                          </div>
                        </div>
                        <div className="flex flex-col items-start gap-1">
                          <span className="text-light-text-secondary dark:text-gray-300">
                            Not Started
                          </span>
                          <div className="flex items-center">
                            <div
                              className={`w-3 h-3 rounded-full ${getDashboardStatusColor(DashboardStatus.NOT_STARTED)}`}
                            ></div>
                            <AnimatedNumberShared
                              className="ml-2 text-light-text-primary dark:text-gray-300 font-medium"
                              value={Number(effectiveStats.not_started) || 0}
                            />
                          </div>
                        </div>
                        <div className="flex flex-col items-start gap-1">
                          <span className="text-light-text-secondary dark:text-gray-300">
                            On Track
                          </span>
                          <div className="flex items-center">
                            <div
                              className={`w-3 h-3 rounded-full ${getDashboardStatusColor(DashboardStatus.ON_TRACK)}`}
                            ></div>
                            <AnimatedNumberShared
                              className="ml-2 text-light-text-primary dark:text-gray-300 font-medium"
                              value={Number(effectiveStats.on_track) || 0}
                            />
                          </div>
                        </div>
                        <div className="flex flex-col items-start gap-1">
                          <span className="text-light-text-secondary dark:text-gray-300">
                            Closed
                          </span>
                          <div className="flex items-center">
                            <div
                              className={`w-3 h-3 rounded-full ${getDashboardStatusColor(DashboardStatus.CLOSED)}`}
                            ></div>
                            <AnimatedNumberShared
                              className="ml-2 text-light-text-primary dark:text-gray-300 font-medium"
                              value={Number(effectiveStats.closed) || 0}
                            />
                          </div>
                        </div>
                        <div className="flex items-center justify-center">
                          <button
                            onClick={() => setShowAllStatuses(false)}
                            className="text-light-text-secondary dark:text-gray-400 hover:text-light-text-primary dark:hover:text-gray-300 transition-colors duration-200 p-1"
                          >
                            <HiChevronDown className="w-4 h-4 rotate-90" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* <div className="flex flex-col items-start gap-1">
                  <span className="text-light-text-secondary dark:text-gray-300">On Track</span>
                  <div className="flex items-center">
                    <div className={`w-3 h-3 rounded-full ${getDashboardStatusColor(DashboardStatus.ON_TRACK)}`}></div>
                    <span className="ml-1 text-light-text-primary dark:text-gray-300 font-medium">{stats.on_track}</span>
                  </div>
                </div> */}
                  {/* <div className="flex flex-col items-start gap-1">
                  <span className="text-light-text-secondary dark:text-gray-300">Critical</span>
                  <div className="flex items-center">
                    <div className={`w-3 h-3 rounded-full ${getDashboardStatusColor(DashboardStatus.CRITICAL)}`}></div>
                   
                    <span className="ml-1 text-light-text-primary dark:text-gray-300 font-medium">{stats.delayed_critical}</span>
                  </div>
                </div> */}
                  {/* <div className="flex flex-col items-start gap-1">
                  <span className="text-light-text-secondary dark:text-gray-300">Running</span>
                  <div className="flex items-center">
                    <div className={`w-3 h-3 rounded-full ${getDashboardStatusColor(DashboardStatus.RUNNING)}`}></div>
                    <span className="ml-1 text-light-text-primary dark:text-gray-300 font-medium">{stats.running}</span>
                  </div>
                </div>
                <div className="flex flex-col items-start gap-1">
                  <span className="text-light-text-secondary dark:text-gray-300">Waiting</span>
                  <div className="flex items-center">
                    <div className={`w-3 h-3 rounded-full ${getDashboardStatusColor(DashboardStatus.WAITING)}`}></div>
                   
                    <span className="ml-1 text-light-text-primary dark:text-gray-300 font-medium">{stats.waiting}</span>
                  </div>
                </div> */}
                </>
              )}

            {!isTemplate &&
              currentEntity === ENTITY.ROADMAP &&
              Object.keys(effectiveStats).length > 0 && (
                <>
                  <>
                    <div
                      className={`flex items-start gap-4 transition-all duration-300 ease-out`}
                    >
                      <div className="flex flex-col items-start gap-1">
                        <span className="text-light-text-secondary dark:text-gray-300 inline-flex items-center gap-1">
                          <AnimatedWidthSlot
                            show={effectiveStats.at_risk > 0}
                            width={ICON_SLOT_PX}
                          >
                            <Tooltip
                              content="Contributes to Risk"
                              placement="top"
                            >
                              <FiAlertTriangle className="text-red-500 transition-all duration-300 opacity-100 scale-100" />
                            </Tooltip>
                          </AnimatedWidthSlot>
                          At Risk
                        </span>
                        <div
                          className={`flex items-center ${!effectiveStats.at_risk > 0 ? "ml-1" : ""}`}
                        >
                          <div
                            className={`w-3 h-3 rounded-full ${getDashboardStatusColor(DashboardStatus.AT_RISK)}`}
                          ></div>
                          <AnimatedNumberShared
                            className="ml-2 text-light-text-primary dark:text-gray-300 font-medium"
                            value={Number(effectiveStats.at_risk) || 0}
                          />
                        </div>
                      </div>
                      <div className="flex flex-col items-start gap-1">
                        <span className="text-light-text-secondary dark:text-gray-300 inline-flex items-center gap-1">
                         
                          On Track
                        </span>
                        <div
                          className={`flex items-center ${!effectiveStats.on_track > 0 ? "ml-1" : ""}`}
                        >
                          <div
                            className={`w-3 h-3 rounded-full ${getDashboardStatusColor(DashboardStatus.ON_TRACK)}`}
                          ></div>
                          <AnimatedNumberShared
                            className="ml-2 text-light-text-primary dark:text-gray-300 font-medium"
                            value={Number(effectiveStats.on_track) || 0}
                          />
                        </div>
                      </div>
                      <div className="flex flex-col items-start gap-1">
                        <span className="text-light-text-secondary dark:text-gray-300 inline-flex items-center gap-1">
                         
                          Completed
                        </span>
                        <div
                          className={`flex items-center ${!effectiveStats.completed > 0 ? "ml-1" : ""}`}
                        >
                          <div
                            className={`w-3 h-3 rounded-full ${getDashboardStatusColor(DashboardStatus.COMPLETED)}`}
                          ></div>
                          <AnimatedNumberShared
                            className="ml-2 text-light-text-primary dark:text-gray-300 font-medium"
                            value={Number(effectiveStats.completed) || 0}
                          />
                        </div>
                      </div>

                      {/* Others expandable section for roadmap */}
                      {!showAllStatuses && (
                        <div className="relative">
                          <button
                            onClick={() => setShowAllStatuses(!showAllStatuses)}
                            className="flex flex-col items-start gap-1 hover:opacity-80 transition-opacity duration-200"
                          >
                            <span className="text-light-text-secondary dark:text-gray-300 inline-flex items-center gap-1">
                              Others
                              <HiChevronDown className="w-3 h-3 -rotate-90" />
                            </span>
                            <div className="flex items-center">
                              <div
                                className="w-3 h-3 rounded-full"
                                style={{
                                  background:
                                    "conic-gradient(#3b82f6 0% 25%, #8b5cf6 25% 50%, #22c55e 50% 75%, #6b7280 75% 100%)",
                                }}
                              ></div>
                              <span className="ml-1 text-light-text-primary dark:text-gray-300 font-medium">
                                {(effectiveStats.completed ?? 0) +
                                  (effectiveStats.not_started ?? 0) +
                                  (effectiveStats.on_track ?? 0) +
                                  (effectiveStats.closed ?? 0)}
                              </span>
                            </div>
                          </button>
                        </div>
                      )}

                      {/* Expanded statuses - slide in from right */}
                      {showAllStatuses && (
                        <div className="flex items-start gap-4 transition-all duration-300 ease-out">
                         
                          <div className="flex flex-col items-start gap-1">
                            <span className="text-light-text-secondary dark:text-gray-300">
                              Not Started
                            </span>
                            <div className="flex items-center">
                              <div
                                className={`w-3 h-3 rounded-full ${getDashboardStatusColor(DashboardStatus.NOT_STARTED)}`}
                              ></div>
                              <span className="ml-1 text-light-text-primary dark:text-gray-300 font-medium">
                                {effectiveStats.not_started ?? 0}
                              </span>
                            </div>
                          </div>
                        
                          <div className="flex flex-col items-start gap-1">
                            <span className="text-light-text-secondary dark:text-gray-300">
                              Closed
                            </span>
                            <div className="flex items-center">
                              <div
                                className={`w-3 h-3 rounded-full ${getDashboardStatusColor(DashboardStatus.CLOSED)}`}
                              ></div>
                              <span className="ml-1 text-light-text-primary dark:text-gray-300 font-medium">
                                {effectiveStats.closed ?? 0}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center justify-center">
                            <button
                              onClick={() => setShowAllStatuses(false)}
                              className="text-light-text-secondary dark:text-gray-400 hover:text-light-text-primary dark:hover:text-gray-300 transition-colors duration-200 p-1"
                            >
                              <HiChevronDown className="w-4 h-4 rotate-90" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                </>
              )}

            {displayDate && (
              <div className="flex flex-col items-start ml-3 gap-1.5 min-w-[185px]">
                <span className="text-light-text-secondary dark:text-gray-300 text-xs">
                  Start and end date
                </span>
                <span className="text-light-text-primary dark:text-gray-300 font-medium text-sm">
                  {formatDateRange(displayDate)}
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TimelineNavBar; 